/**
 * WaterTypeBadge 组件
 * 用于显示鱼缸的水体类型（淡水/海水）
 * 淡水：翠绿色 + 🌿
 * 海水：深蓝色 + 🪸
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export type WaterType = "freshwater" | "saltwater" | "brackish";

interface WaterTypeBadgeProps {
  /** 水体类型 */
  waterType: WaterType;
  /** 是否显示文本标签 */
  showLabel?: boolean;
  /** 显示变体：default 带背景色，plain 只显示图标+文字，solid 纯色背景适合图片叠加 */
  variant?: "default" | "plain" | "solid";
  /** 自定义类名 */
  className?: string;
}

/**
 * 水体类型徽章组件
 * 根据水体类型显示不同的颜色和图标
 */
export function WaterTypeBadge({
  waterType,
  showLabel = true,
  variant = "default",
  className,
}: WaterTypeBadgeProps) {
  const { t } = useTranslation("tanks");

  const config = {
    freshwater: {
      icon: "🌿",
      plainColor: "text-emerald-600",
      solidColor:
        "bg-slate-900/85 hover:bg-slate-900 text-white border border-white/20 shadow-sm", // Solid uses same dark style for all usually, but strict mapping if desired
      defaultStyle:
        "bg-emerald-500/90 hover:bg-emerald-500 text-white border-emerald-600/20",
    },
    saltwater: {
      icon: "🪸",
      plainColor: "text-blue-600",
      solidColor:
        "bg-slate-900/85 hover:bg-slate-900 text-white border border-white/20 shadow-sm",
      defaultStyle:
        "bg-blue-700/90 hover:bg-blue-700 text-white border-blue-800/20",
    },
    brackish: {
      icon: "🌊",
      plainColor: "text-amber-600",
      solidColor:
        "bg-slate-900/85 hover:bg-slate-900 text-white border border-white/20 shadow-sm",
      defaultStyle:
        "bg-amber-500/90 hover:bg-amber-500 text-white border-amber-600/20",
    },
  };

  const typeConfig = config[waterType] || config.freshwater;
  const { icon } = typeConfig;
  const label = t(`waterType.${waterType}`);

  // plain 变体：只显示图标+文字，无背景色
  if (variant === "plain") {
    return (
      <span
        className={cn(
          "inline-flex items-center text-sm",
          typeConfig.plainColor,
          className,
        )}
      >
        <span>{icon}</span>
        {showLabel && <span className="ml-1">{label}</span>}
      </span>
    );
  }

  // solid 变体：纯色背景徽章，适合叠加在图片上
  // 使用深色背景确保在亮色/暗色主题和任意图片背景下都有良好对比度
  if (variant === "solid") {
    return (
      <Badge
        className={cn(
          "bg-slate-900/85 hover:bg-slate-900 text-white border border-white/20 shadow-sm",
          className,
        )}
      >
        <span className="text-xs">{icon}</span>
        {showLabel && <span className="ml-1 text-xs font-medium">{label}</span>}
      </Badge>
    );
  }

  // default 变体：带背景色的徽章
  return (
    <Badge className={cn(typeConfig.defaultStyle, className)}>
      <span className="text-sm">{icon}</span>
      {showLabel && <span className="ml-1">{label}</span>}
    </Badge>
  );
}

export default WaterTypeBadge;
